import { Button, ButtonGroup, Stack, Tooltip, Wrap } from '@chakra-ui/react';
import {
	ACCOUNT_TYPE,
	adoOrganizationNamespaces,
	adoProjectNamespaces,
	connectionNameSpaces,
	EnvironmentTypesEnum,
	providerNameSpaces,
	repositoryBranchNamespaces,
	repositoryIdNamespaces,
	repositoryNameSpaces,
	sourceControlIntegrationIdNameSpaces,
	TFormikCommonProps,
} from '../../pages/create-project/helpers';
import {
	connections,
	secondStep,
	systemHealthStatus,
} from '../../../@xmcloud/core/messages/en';
import {
	EnumInNumberSourceControl,
	ESourceControl,
	SourceControlType,
} from '../../../@xmcloud/types';
import {
	FormControl,
	HeadingMd,
	SelectFormControl,
	XMCSButton,
} from '../../shared-components';
import { FormikValues } from '../../pages/create-project/hooks/CreateProjectContext';
import {
	adoIntegrationUrl,
	githubIntegrationUrl,
	NEW_SOURCE_CONTROL_ACCOUNT,
} from '../../../@xmcloud/utils/constants';
import {
	useGetRepositoryIntegrations,
	useRepositoryIntegrateState,
} from '../../services/repositories';
import { useEffect, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import config from '../../config/config';
import { useComponentHealthy } from '../../../@xmcloud/hooks';

const { github, ado, sourceControlProvider } = connections;
const {
	byocProviderHelpertext,
	templateProviderHelpertext,
	addAzure,
	addGithub,
	azureAccount,
	githubAccount,
} = secondStep;
const { githubStatus, adoStatus } = systemHealthStatus;

const { validate_integration_V2 } = config.repository;

const { GitHub, ADO } = ESourceControl;
const { GitHub: GitHubInNumber, ADO: AdoInNumber } = EnumInNumberSourceControl;

export const SetupProviderAccount: React.FC<
	{
		isByoc?: boolean;
		isCmEnv?: boolean;
		nameSpace?: EnvironmentTypesEnum | 'default';
		isIntegrationIdValidationValid: boolean;
		integrationValidationErrorMsg: string;
		isSourceControlIntegrationLoading: boolean;
		setValues: (values: any) => void | any;
		setTouched: any;
		setErrors: any;
		setFieldTouched: any;
		onChangeProvider?: (provider: SourceControlType) => void;
		/** When the env's connection is not in the user's list (e.g. another org), pass id + label so the account dropdown can display it */
		initialIntegrationId?: string;
		initialIntegrationLabel?: string;
		/** Only inject "initial connection" option when this matches the selected provider */
		initialIntegrationProvider?: SourceControlType | '';
		/** When true, show message as warning only (no red border on account field) */
		isConnectionFromAnotherOrg?: boolean;
		/** When user re-selects back the "another org" connection, set these so form matches initial state */
		initialRepository?: string;
		initialRepositoryId?: string;
		initialRepositoryBranch?: string;
		initialAdoOrganization?: string;
		initialAdoProjectName?: string;
	} & TFormikCommonProps
> = ({
	isByoc = true,
	isCmEnv = true,
	values,
	errors,
	touched,
	nameSpace = 'default',
	setValues,
	setTouched,
	setErrors,
	setFieldTouched,
	isIntegrationIdValidationValid,
	integrationValidationErrorMsg,
	isSourceControlIntegrationLoading,
	onChangeProvider,
	initialIntegrationId,
	initialIntegrationLabel,
	initialIntegrationProvider,
	isConnectionFromAnotherOrg,
	initialRepository,
	initialRepositoryId,
	initialRepositoryBranch,
	initialAdoOrganization,
	initialAdoProjectName,
}) => {
	const providerNameSpace = providerNameSpaces[nameSpace];
	const connectionNameSpace = connectionNameSpaces[nameSpace];
	const sourceControlIntegrationIdNameSpace =
		sourceControlIntegrationIdNameSpaces[nameSpace];
	const repositoryNamespace = repositoryNameSpaces[nameSpace];
	const repositoryIdNamespace = repositoryIdNamespaces[nameSpace];
	const repositoryBranchNamespace = repositoryBranchNamespaces[nameSpace];
	const adoOrganizationNamespace = adoOrganizationNamespaces[nameSpace];
	const adoProjectNamespace = adoProjectNamespaces[nameSpace];

	const currentProvider = values[providerNameSpace];

	const isGithub = currentProvider === GitHub;
	const isAdo = currentProvider === ADO;
	const { isADOHealthy, isGithubHealthy } = useComponentHealthy({
		provider: isGithub ? GitHubInNumber : isAdo ? AdoInNumber : 0,
	});

	const queryClient = useQueryClient();

	const {
		data: repoIntegrationData,
		isLoading: isFetchingIntegrationLoading,
	} = useGetRepositoryIntegrations();

	const repositoryIntegrations = useMemo(
		() =>
			repoIntegrationData?.data.filter(
				(i) => i.provider === currentProvider,
			) || [],
		[repoIntegrationData, currentProvider],
	);

	const integrationsOptions = useMemo(
		() =>
			repositoryIntegrations.map(({ account, id }) => ({
				label: account,
				value: id,
			})) || [],
		[repositoryIntegrations],
	);

	const accountOptions = useMemo(() => {
		if (
			!initialIntegrationId ||
			integrationsOptions.some((o) => o.value === initialIntegrationId) ||
			(initialIntegrationProvider !== undefined &&
				initialIntegrationProvider !== currentProvider)
		) {
			return integrationsOptions;
		}

		const label = initialIntegrationLabel?.trim() || 'Current connection';
		return [{ value: initialIntegrationId, label }, ...integrationsOptions];
	}, [
		integrationsOptions,
		initialIntegrationId,
		initialIntegrationLabel,
		initialIntegrationProvider,
		currentProvider,
	]);

	const {
		refetch: fetchIntegrateState,
		isFetching: isFetchingIntegrateState,
	} = useRepositoryIntegrateState({ enabled: false });

	useEffect(() => {
		const channel = new BroadcastChannel(NEW_SOURCE_CONTROL_ACCOUNT);

		channel.addEventListener('message', (e) => {
			if (e.origin !== window.location.origin) return;
			queryClient.invalidateQueries(
				validate_integration_V2.queryKey(e.data.id),
			);
			setTouched({});
			setErrors({});
			setValues((prev: FormikValues) => ({
				...prev,
				[connectionNameSpace]: e.data.account,
				[sourceControlIntegrationIdNameSpace]: e.data.id,
				[providerNameSpace]: e.data.provider,
				[ACCOUNT_TYPE]: e.data.accountType,
				[repositoryNamespace]: '',
				[repositoryIdNamespace]: '',
				[repositoryBranchNamespace]: '',
				[adoOrganizationNamespace]: '',
				[adoProjectNamespace]: '',
			}));
		});

		return () => {
			channel.close();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [setValues]);

	const onProviderChange = (provider: SourceControlType) => {
		if (onChangeProvider) {
			onChangeProvider(provider);
			setTouched({});
			return;
		}
		setValues((prev: FormikValues) => ({
			...prev,
			[repositoryNamespace]: '',
			[repositoryIdNamespace]: '',
			[sourceControlIntegrationIdNameSpace]: '',
			[connectionNameSpace]: '',
			[repositoryBranchNamespace]: '',
			[providerNameSpace]: provider,
		}));
		setTouched({});
	};
	const onConnectionChange = (e: any) => {
		const integration = repositoryIntegrations?.find(
			(i: any) => i.id === e.value,
		);
		if (integration?.provider) {
			setValues((prev: FormikValues) => ({
				...prev,
				[sourceControlIntegrationIdNameSpace]: e.value || '',
				[connectionNameSpace]: integration.account,
				[providerNameSpace]: integration.provider as SourceControlType,
				[ACCOUNT_TYPE]: integration.accountType || 0,
				[repositoryNamespace]: '',
				[repositoryIdNamespace]: '',
				[repositoryBranchNamespace]: '',
				[adoOrganizationNamespace]: '',
				[adoProjectNamespace]: '',
			}));
			return;
		}
		// User re-selected "connection from another org" option (not in repositoryIntegrations)
		if (initialIntegrationId && e.value === initialIntegrationId) {
			setValues((prev: FormikValues) => ({
				...prev,
				[sourceControlIntegrationIdNameSpace]: e.value || '',
				[connectionNameSpace]: initialIntegrationLabel || '',
				[providerNameSpace]: prev[providerNameSpace],
				[repositoryNamespace]: initialRepository ?? '',
				[repositoryIdNamespace]: initialRepositoryId ?? '',
				[repositoryBranchNamespace]: initialRepositoryBranch ?? '',
				[adoOrganizationNamespace]: initialAdoOrganization ?? '',
				[adoProjectNamespace]: initialAdoProjectName ?? '',
			}));
			return;
		}
		// Neither a known integration nor the initial "another org" connection —
		// this shouldn't happen given the options list, so do nothing.
	};
	const currentEnvText = isCmEnv ? 'authoring environment.' : 'editing host.';

	const accountHelperText = isByoc
		? `${byocProviderHelpertext}${currentEnvText}`
		: templateProviderHelpertext;

	return (
		<Stack gap="4">
			<HeadingMd text="Repository" />
			<FormControl
				isRequired={true}
				label={sourceControlProvider}
				error={errors[providerNameSpace] || ''}
			>
				<ButtonGroup
					variant="toggle"
					size="sm"
					layerStyle="toggleGroup"
				>
					<Tooltip label={githubStatus} isDisabled={isGithubHealthy}>
						<Button
							onClick={() => onProviderChange(GitHub)}
							data-testid="github-provider-btn"
							isActive={isGithub}
							isDisabled={!isGithubHealthy}
						>
							{github}
						</Button>
					</Tooltip>
					<Tooltip label={adoStatus} isDisabled={isADOHealthy}>
						<Button
							onClick={() => onProviderChange(ADO)}
							data-testid="ado-provider-btn"
							isActive={isAdo}
							isDisabled={!isADOHealthy}
						>
							{ado}
						</Button>
					</Tooltip>
				</ButtonGroup>
			</FormControl>
			<Stack gap="2">
				<SelectFormControl
					{...{
						isInvalid: Boolean(
							(errors[sourceControlIntegrationIdNameSpace] ||
								(!isIntegrationIdValidationValid &&
									!isConnectionFromAnotherOrg)) &&
								touched[sourceControlIntegrationIdNameSpace],
						),
						isLoading:
							isFetchingIntegrationLoading ||
							isSourceControlIntegrationLoading,
						options: accountOptions,
						onChange: onConnectionChange,
						label: isGithub ? githubAccount : azureAccount,
						name: sourceControlIntegrationIdNameSpace,
						currentValue:
							values[sourceControlIntegrationIdNameSpace],
						error:
							errors[sourceControlIntegrationIdNameSpace] ||
							integrationValidationErrorMsg,
						pt: 2,
						onFocus: () =>
							setFieldTouched(
								sourceControlIntegrationIdNameSpace,
								true,
							),
						isRequired: true,
						textContent: accountHelperText,
					}}
				/>
				<Wrap>
					<XMCSButton
						text={isGithub ? addGithub : addAzure}
						isLoading={isFetchingIntegrateState}
						variant="link"
						onClick={() => {
							if (values[providerNameSpace]) {
								fetchIntegrateState().then(({ data }) => {
									const state = data?.data ?? '';
									window.open(
										isGithub
											? githubIntegrationUrl(state)
											: isAdo
												? adoIntegrationUrl(state)
												: '#',
									);
								});
							}
						}}
					/>
				</Wrap>
			</Stack>
		</Stack>
	);
};
